/**
 * Menu collections filter.
 *
 * Every product is already in the page, so a filter click is a class toggle —
 * no request, no reload, no flash.
 *
 * With a grid per collection it shows and hides whole groups. With one grid it
 * shows and hides individual cards by the collections they belong to.
 */

function init(root) {
  if (root.dataset.menuReady === 'true') return;
  root.dataset.menuReady = 'true';

  const bar = root.querySelector('[data-menu-filter]');
  const resultEl = root.querySelector('[data-menu-result]');
  const noneEl = root.querySelector('[data-menu-none]');
  const groups = [...root.querySelectorAll('[data-menu-group]')];
  const items = [...root.querySelectorAll('[data-menu-item]')];
  const animate = root.dataset.menuAnimate === 'true';

  function replay(el, index) {
    if (!animate) return;
    el.style.animation = 'none';
    void el.offsetWidth; // force a reflow so the entrance plays again
    el.style.animation = '';
    el.style.animationDelay = `${Math.min(index * 0.035, 0.5)}s`;
  }

  function apply(handle, label) {
    let shown = 0;

    if (groups.length) {
      for (const group of groups) {
        const hit = !handle || group.dataset.menuGroup === handle;
        group.classList.toggle('is-hidden', !hit);
        if (!hit) continue;
        group.querySelectorAll('[data-menu-item]').forEach((item, i) => {
          shown += 1;
          replay(item, i);
        });
      }
    } else {
      for (const item of items) {
        const list = item.dataset.menuIn || '';
        const hit = !handle || list.includes(`|${handle}|`);
        item.classList.toggle('is-hidden', !hit);
        if (hit) {
          shown += 1;
          replay(item, shown);
        }
      }
    }

    if (resultEl) {
      resultEl.textContent =
        `${shown} ${shown === 1 ? 'product' : 'products'}` + (label ? ` in ${label}` : '');
    }
    if (noneEl) noneEl.hidden = shown !== 0;
  }

  if (!bar) {
    apply('', '');
    return;
  }

  /* ---- phone: the bar opens as a sheet ---- */
  const trigger = root.querySelector('[data-menu-filter-trigger]');
  const backdrop = root.querySelector('[data-menu-filter-backdrop]');
  const closeButton = root.querySelector('[data-menu-filter-close]');
  const currentLabel = root.querySelector('[data-menu-filter-current]');
  const currentCount = root.querySelector('[data-menu-filter-count]');

  function openSheet() {
    if (!backdrop) return;
    backdrop.hidden = false;
    // Next frame, so the transition has a start value to move from.
    requestAnimationFrame(() => backdrop.classList.add('is-open'));
    bar.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('menu-filter-open');
    bar.querySelector('.menu-filt[aria-pressed="true"]')?.focus();
  }

  function closeSheet() {
    if (!backdrop) return;
    backdrop.classList.remove('is-open');
    bar.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-filter-open');
    window.setTimeout(() => {
      if (!bar.classList.contains('is-open')) backdrop.hidden = true;
    }, 300);
  }

  /** Mirrors the chosen collection onto the trigger, which is all a phone shows. */
  function paintTrigger(button) {
    if (!currentLabel) return;
    currentLabel.textContent = button?.querySelector('span')?.textContent.trim() ?? '';
    if (currentCount) currentCount.textContent = button?.querySelector('i')?.textContent.trim() ?? '';
  }

  trigger?.addEventListener('click', openSheet);
  closeButton?.addEventListener('click', closeSheet);
  backdrop?.addEventListener('click', closeSheet);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && bar.classList.contains('is-open')) closeSheet();
  });

  bar.addEventListener('click', (event) => {
    const button = event.target.closest('.menu-filt');
    if (!button) return;

    for (const other of bar.querySelectorAll('.menu-filt')) {
      other.setAttribute('aria-pressed', String(other === button));
    }
    paintTrigger(button);
    closeSheet();
    apply(button.dataset.menuFor, button.querySelector('span')?.textContent.trim());

    // Keep the bar in view when a short collection leaves the page scrolled
    // past the top of the section.
    if (root.getBoundingClientRect().top < 0) {
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  const active = bar.querySelector('.menu-filt[aria-pressed="true"]');
  paintTrigger(active);
  apply(active?.dataset.menuFor ?? '', active?.querySelector('span')?.textContent.trim());
}

function boot(scope = document) {
  scope.querySelectorAll('[data-menu-collections]').forEach(init);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot());
} else {
  boot();
}

// Re-run when the theme editor re-renders the section.
document.addEventListener('shopify:section:load', (event) => boot(event.target));